/**
 * PTEL Tool - Coordinate Parser
 * Parser universal para coordenadas en diferentes formatos
 * 
 * Formatos soportados:
 * - DMS Google Maps: 37°11'03.5"N 3°36'21.5"W
 * - DMS Wikipedia: 37° 11′ 03″ N, 3° 36′ 22″ O
 * - DMS español: 37º11'03"N 3º36'21"O
 * - DD (Decimal Degrees): 37.184306, -3.605972
 * - UTM: 447850.23, 4116234.56
 * 
 * @version 2.0.0
 * @task E04
 * @date 2025-12-11
 */

import { fromUtm } from './projections';
import { fixOCR, hasOCRErrors } from '../lib/ocrCorrector';
import { correctDmsOverflow, hasDmsOverflow } from '../services/normalization/DmsOverflowCorrector';
import { recoverTruncatedY } from '../services/normalization/YTruncationRecovery';
import { splitConcatenatedCoords } from '../services/normalization/ConcatenatedCoordSplitter';
import { OpenLocationCode } from 'open-location-code';

/** Instancia singleton del codec OLC (M5) */
const _olc = new OpenLocationCode();

// ============================================================================
// TYPES
// ============================================================================

export type CoordinateFormat = 
  | 'DMS_GOOGLE'      // 37°11'03.5"N 3°36'21.5"W
  | 'DMS_WIKIPEDIA'   // 37° 11′ 03″ N, 3° 36′ 22″ O
  | 'DMS_SPANISH'     // 37º11'03"N 3º36'21"O
  | 'DMS_SPACES'      // 37 11 03 N 3 36 21 W
  | 'DD'              // 37.184306, -3.605972 (E04)
  | 'UTM'             // 447850.23, 4116234.56 (E04)
  | 'PLUSCODE'        // 8C9P2Q+XC · Google Open Location Code (M5)
  | 'UNKNOWN';

export interface ParsedCoordinate {
  /** Latitud en grados decimales (WGS84) */
  lat: number;
  /** Longitud en grados decimales (WGS84) */
  lon: number;
  /** Formato detectado */
  format: CoordinateFormat;
  /** Confianza del parsing (0-100) */
  confidence: number;
  /** Indica si está en zona Huso 29 (lon < -6°) */
  inHuso29Zone: boolean;
  /** Texto original parseado */
  originalText: string;
  /** Warnings o notas */
  warnings?: string[];
}

export interface DMSComponents {
  degrees: number;
  minutes: number;
  seconds: number;
  direction: 'N' | 'S' | 'E' | 'W';
}

// ============================================================================
// CONSTANTS
// ============================================================================

/** Rangos válidos para España/Andalucía */
export const SPAIN_BOUNDS = {
  lat: { min: 35, max: 44 },
  lon: { min: -10, max: 5 },
};

/** Rangos más estrictos para Andalucía */
export const ANDALUCIA_BOUNDS = {
  lat: { min: 36, max: 38.8 },
  lon: { min: -7.5, max: -1.6 },
};

/** Meridiano divisorio entre Huso 29 y Huso 30 */
export const HUSO_29_MERIDIAN = -6;

/** Rangos UTM válidos para Andalucía - Huso 30 (EPSG:25830) */
export const UTM30_ANDALUCIA_BOUNDS = {
  x: { min: 100000, max: 620000 },
  y: { min: 3980000, max: 4290000 },
};

/** Rangos UTM válidos para Huso 29 (EPSG:25829) - Huelva/oeste Sevilla */
export const UTM29_BOUNDS = {
  x: { min: 80000, max: 210000 },
  y: { min: 3980000, max: 4200000 },
};

// ============================================================================
// B12: NORMALIZACIÓN FORMATO EUROPEO (punto miles, coma decimal)
// ============================================================================

/**
 * Normaliza un número con formato europeo a formato estándar.
 * 
 * Formatos europeos soportados:
 * - SPANISH_FORMAT_LONG: "4.077.905,68" → 4077905.68 (punto de miles largo)
 * - SPANISH_FORMAT_SHORT: "504.352,98" → 504352.98 (punto de miles corto)
 * - DOUBLE_DOT: "4 076 464..97" → 4076464.97 (doble punto como decimal)
 * - SPACE_THOUSANDS: "4 077 905" → 4077905 (espacios como separador miles)
 * - COMMA_DECIMAL: "504750,23" → 504750.23 (coma decimal simple)
 * 
 * Heurística de detección:
 * - Si hay coma seguida de 1-2 dígitos al final → formato europeo
 * - Si hay punto seguido de exactamente 3 dígitos (no al final) → punto de miles
 * - Si hay doble punto ".." → error tipográfico, segundo punto es decimal
 * - Si hay espacios entre grupos de dígitos → separador de miles
 * 
 * @param value - Cadena con número en cualquier formato
 * @returns Número normalizado con punto decimal, o NaN si no es válido
 * 
 * @example
 * normalizeSpanishNumber("4.077.905,68")  // 4077905.68
 * normalizeSpanishNumber("504.352,98")    // 504352.98
 * normalizeSpanishNumber("4 076 464..97") // 4076464.97
 * normalizeSpanishNumber("447850.23")     // 447850.23 (ya estándar)
 * normalizeSpanishNumber("4077905")       // 4077905 (entero)
 * 
 * @task B12
 * @date 2025-12-27
 */
export function normalizeSpanishNumber(value: string): number {
  if (!value || typeof value !== 'string') {
    return NaN;
  }

  // Limpiar espacios externos
  let cleaned = value.trim();
  
  // Si está vacío después de limpiar, retornar NaN
  if (cleaned.length === 0) {
    return NaN;
  }

  // 1. DOUBLE_DOT: "4 076 464..97" → reemplazar ".." por "."
  if (cleaned.includes('..')) {
    cleaned = cleaned.replace('..', '.');
  }

  // 2. SPACE_THOUSANDS: "4 077 905" o "4 077 905,68" → eliminar espacios
  // Detectar espacios entre dígitos (separador de miles)
  if (/\d\s+\d/.test(cleaned)) {
    cleaned = cleaned.replace(/\s+/g, '');
  }

  // 3. Detectar formato europeo vs anglosajón
  // Formato europeo: coma como decimal, punto como miles
  // Formato anglosajón: punto como decimal, coma como miles
  
  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');
  
  if (hasComma && hasDot) {
    // Ambos presentes: determinar cuál es decimal
    const lastCommaPos = cleaned.lastIndexOf(',');
    const lastDotPos = cleaned.lastIndexOf('.');
    
    if (lastCommaPos > lastDotPos) {
      // Coma después del punto → FORMATO EUROPEO
      // "4.077.905,68" → eliminar puntos, coma→punto
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      // Punto después de coma → FORMATO ANGLOSAJÓN ("4,077,905.68")
      // Eliminar comas (separador miles)
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (hasComma && !hasDot) {
    // Solo coma: ¿decimal o miles?
    // Heurística: si después de la coma hay 1-2 dígitos → decimal
    // Si hay exactamente 3 dígitos → miles (raro en coordenadas)
    const afterComma = cleaned.split(',')[1];
    
    if (afterComma && afterComma.length <= 2) {
      // "504750,23" → coma es decimal
      cleaned = cleaned.replace(',', '.');
    } else if (afterComma && afterComma.length === 3 && cleaned.split(',').length === 2) {
      // "504,750" → posiblemente miles (formato US), mantener o quitar coma
      // En contexto de coordenadas UTM, valores de 3 dígitos tras coma son raros
      // Asumir que es formato anglosajón de miles
      cleaned = cleaned.replace(',', '');
    } else {
      // Caso ambiguo: "4077905,123" - 3 decimales es posible
      // Asumir coma decimal
      cleaned = cleaned.replace(',', '.');
    }
  } else if (hasDot && !hasComma) {
    // Solo punto: verificar si es separador de miles español
    // Patrón: punto seguido de exactamente 3 dígitos, posiblemente repetido
    // "4.077.905" → punto de miles
    // "447850.23" → punto decimal
    
    const parts = cleaned.split('.');
    if (parts.length >= 2) {
      // Verificar si TODOS los segmentos después del primero tienen exactamente 3 dígitos
      const allThreeDigits = parts.slice(1).every(p => /^\d{3}$/.test(p));
      
      if (allThreeDigits && parts.length >= 2) {
        // "4.077.905" o "504.352" → puntos son miles
        cleaned = cleaned.replace(/\./g, '');
      }
      // Si no, asumir que el último punto es decimal ("447850.23")
    }
  }
  
  // 4. Parsear el resultado
  const result = parseFloat(cleaned);
  
  return result;
}

/**
 * Detecta si un valor de coordenada usa formato europeo.
 * Útil para logging y warnings.
 * 
 * @param value - Cadena a analizar
 * @returns Tipo de formato detectado
 * 
 * @task B12
 */
export function detectNumberFormat(value: string): 
  | 'SPANISH_FORMAT_LONG'   // "4.077.905,68"
  | 'SPANISH_FORMAT_SHORT'  // "504.352,98"
  | 'DOUBLE_DOT'            // "4 076 464..97"
  | 'SPACE_THOUSANDS'       // "4 077 905"
  | 'COMMA_DECIMAL'         // "504750,23"
  | 'STANDARD'              // "447850.23" o "4077905"
  | 'UNKNOWN'
{
  if (!value || typeof value !== 'string') {
    return 'UNKNOWN';
  }
  
  const trimmed = value.trim();
  
  // Doble punto
  if (trimmed.includes('..')) {
    return 'DOUBLE_DOT';
  }
  
  // Espacios como separador de miles
  if (/\d\s+\d/.test(trimmed)) {
    return 'SPACE_THOUSANDS';
  }
  
  const hasComma = trimmed.includes(',');
  const hasDot = trimmed.includes('.');
  
  // Formato español largo: punto miles + coma decimal
  if (hasComma && hasDot) {
    const lastComma = trimmed.lastIndexOf(',');
    const lastDot = trimmed.lastIndexOf('.');
    if (lastComma > lastDot) {
      // Contar puntos para distinguir largo de corto
      const dotCount = (trimmed.match(/\./g) || []).length;
      return dotCount >= 2 ? 'SPANISH_FORMAT_LONG' : 'SPANISH_FORMAT_SHORT';
    }
  }
  
  // Solo coma (probable decimal europeo)
  if (hasComma && !hasDot) {
    const afterComma = trimmed.split(',')[1];
    if (afterComma && afterComma.length <= 2) {
      return 'COMMA_DECIMAL';
    }
  }
  
  // Solo punto: verificar si es miles
  if (hasDot && !hasComma) {
    const parts = trimmed.split('.');
    if (parts.length >= 2 && parts.slice(1).every(p => /^\d{3}$/.test(p))) {
      return parts.length >= 3 ? 'SPANISH_FORMAT_LONG' : 'SPANISH_FORMAT_SHORT';
    }
  }
  
  return 'STANDARD';
}

// ============================================================================
// DMS REGEX PATTERNS
// ============================================================================

/**
 * Patrones para detectar coordenadas DMS
 * Captura: grados, minutos, segundos (con decimales), dirección
 */

// Símbolos de grado: ° º ˚
const DEG_SYMBOLS = '[°º˚]';

// Símbolos de minuto: ' ′ ʹ '
const MIN_SYMBOLS = "['''′ʹ]";

// Símbolos de segundo: " ″ ʺ '' "
const SEC_SYMBOLS = '["""″ʺ]|\'\'';

// Direcciones: N/S para latitud, E/W/O para longitud
const _LAT_DIR = '[NSns]';
const _LON_DIR = '[EWOewo]';

/**
 * Patrón DMS completo con símbolos
 * Ejemplos: 37°11'03.5"N, 37º11'03"N, 37° 11′ 03″ N
 */
const DMS_PATTERN = new RegExp(
  `(\\d{1,3})\\s*${DEG_SYMBOLS}\\s*` +           // Grados
  `(\\d{1,2})\\s*${MIN_SYMBOLS}\\s*` +           // Minutos
  `(\\d{1,2}(?:[.,]\\d+)?)\\s*(?:${SEC_SYMBOLS})?\\s*` + // Segundos (opcional símbolo)
  `([NSEWOnsewо])`,                               // Dirección
  'g'
);

/**
 * Patrón DMS con espacios (sin símbolos)
 * Ejemplo: 37 11 03 N 3 36 21 W
 */
const DMS_SPACES_PATTERN = /(\d{1,3})\s+(\d{1,2})\s+(\d{1,2}(?:[.,]\d+)?)\s*([NSEWOnsewо])/gi;

/**
 * Patrón para par de coordenadas DMS (lat + lon)
 * Detecta dos coordenadas DMS consecutivas
 */
const DMS_PAIR_PATTERN = new RegExp(
  // Latitud
  `(\\d{1,2})\\s*${DEG_SYMBOLS}\\s*` +
  `(\\d{1,2})\\s*${MIN_SYMBOLS}\\s*` +
  `(\\d{1,2}(?:[.,]\\d+)?)\\s*(?:${SEC_SYMBOLS})?\\s*` +
  `([NS])` +
  // Separador
  `[\\s,]+` +
  // Longitud
  `(\\d{1,3})\\s*${DEG_SYMBOLS}\\s*` +
  `(\\d{1,2})\\s*${MIN_SYMBOLS}\\s*` +
  `(\\d{1,2}(?:[.,]\\d+)?)\\s*(?:${SEC_SYMBOLS})?\\s*` +
  `([EWO])`,
  'i'
);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Convierte DMS a grados decimales
 */
export function dmsToDecimal(dms: DMSComponents): number {
  const { degrees, minutes, seconds, direction } = dms;
  
  let decimal = degrees + (minutes / 60) + (seconds / 3600);
  
  // Aplicar signo según dirección
  if (direction === 'S' || direction === 'W') {
    decimal = -decimal;
  }
  
  return decimal;
}

/**
 * F2.01: Corrige overflow DMS in-place en DMSComponents.
 * Retorna true si hubo corrección.
 */
function fixDmsComponentsOverflow(c: DMSComponents): boolean {
  let fixed = false;
  if (c.seconds >= 60) {
    const extraMin = Math.floor(c.seconds / 60);
    c.seconds = c.seconds % 60;
    c.minutes += extraMin;
    fixed = true;
  }
  if (c.minutes >= 60) {
    const extraDeg = Math.floor(c.minutes / 60);
    c.minutes = c.minutes % 60;
    c.degrees += extraDeg;
    fixed = true;
  }
  return fixed;
}

/**
 * Normaliza dirección: O → W (español a inglés)
 */
export function normalizeDirection(dir: string): 'N' | 'S' | 'E' | 'W' {
  const upper = dir.toUpperCase();
  
  if (upper === 'O') return 'W'; // Oeste → West
  if (upper === 'N') return 'N';
  if (upper === 'S') return 'S';
  if (upper === 'E') return 'E';
  if (upper === 'W') return 'W';
  
  throw new Error(`Dirección no válida: ${dir}`);
}

/**
 * Valida si las coordenadas están dentro de España
 */
export function isInSpainBounds(lat: number, lon: number): boolean {
  return (
    lat >= SPAIN_BOUNDS.lat.min &&
    lat <= SPAIN_BOUNDS.lat.max &&
    lon >= SPAIN_BOUNDS.lon.min &&
    lon <= SPAIN_BOUNDS.lon.max
  );
}

/**
 * Valida si las coordenadas están dentro de Andalucía
 */
export function isInAndaluciaBounds(lat: number, lon: number): boolean {
  return (
    lat >= ANDALUCIA_BOUNDS.lat.min &&
    lat <= ANDALUCIA_BOUNDS.lat.max &&
    lon >= ANDALUCIA_BOUNDS.lon.min &&
    lon <= ANDALUCIA_BOUNDS.lon.max
  );
}

/**
 * Determina si la longitud está en zona Huso 29
 */
export function isInHuso29Zone(lon: number): boolean {
  return lon < HUSO_29_MERIDIAN;
}

/**
 * Detecta el formato DMS específico
 */
export function detectDMSFormat(text: string): CoordinateFormat {
  // Google Maps: usa ° ' "
  if (/\d°\d+'[\d.]+[""]/.test(text)) {
    return 'DMS_GOOGLE';
  }
  
  // Wikipedia: usa ° ′ ″ con espacios
  if (/\d°\s*\d+′\s*\d+″/.test(text)) {
    return 'DMS_WIKIPEDIA';
  }
  
  // Español: usa º
  if (/\d[ºo]\d+'/.test(text)) {
    return 'DMS_SPANISH';
  }
  
  // Con espacios sin símbolos
  if (/\d+\s+\d+\s+\d+\s*[NSEW]/i.test(text)) {
    return 'DMS_SPACES';
  }
  
  return 'UNKNOWN';
}

/**
 * Calcula confianza basada en validación
 */
function calculateConfidence(
  lat: number,
  lon: number,
  format: CoordinateFormat
): number {
  let confidence = 50; // Base
  
  // +20 si está en España
  if (isInSpainBounds(lat, lon)) {
    confidence += 20;
  }
  
  // +20 si está en Andalucía
  if (isInAndaluciaBounds(lat, lon)) {
    confidence += 20;
  }
  
  // +10 si formato conocido
  if (format !== 'UNKNOWN') {
    confidence += 10;
  }
  
  return Math.min(confidence, 100);
}

// ============================================================================
// MAIN PARSER FUNCTIONS
// ============================================================================

/**
 * Parsea una coordenada DMS individual
 * 
 * @param text - Texto con coordenada DMS (ej: "37°11'03.5"N")
 * @returns Componentes DMS o null si no es válido
 */
export function parseSingleDMS(text: string): DMSComponents | null {
  // Limpiar texto
  const cleaned = text.trim();
  
  // Intentar patrón con símbolos
  const symbolMatch = cleaned.match(
    new RegExp(
      `(\\d{1,3})\\s*${DEG_SYMBOLS}\\s*` +
      `(\\d{1,2})\\s*${MIN_SYMBOLS}\\s*` +
      `(\\d{1,2}(?:[.,]\\d+)?)\\s*(?:${SEC_SYMBOLS})?\\s*` +
      `([NSEWOnsewо])`,
      'i'
    )
  );
  
  if (symbolMatch) {
    const components: DMSComponents = {
      degrees: parseInt(symbolMatch[1], 10),
      minutes: parseInt(symbolMatch[2], 10),
      seconds: parseFloat(symbolMatch[3].replace(',', '.')),
      direction: normalizeDirection(symbolMatch[4]),
    };
    fixDmsComponentsOverflow(components); // F2.01
    return components;
  }
  
  // Intentar patrón con espacios
  const spacesMatch = cleaned.match(
    /(\d{1,3})\s+(\d{1,2})\s+(\d{1,2}(?:[.,]\d+)?)\s*([NSEWOnsewо])/i
  );
  
  if (spacesMatch) {
    const components: DMSComponents = {
      degrees: parseInt(spacesMatch[1], 10),
      minutes: parseInt(spacesMatch[2], 10),
      seconds: parseFloat(spacesMatch[3].replace(',', '.')),
      direction: normalizeDirection(spacesMatch[4]),
    };
    fixDmsComponentsOverflow(components); // F2.01
    return components;
  }
  
  return null;
}

/**
 * Parsea un par de coordenadas DMS (latitud + longitud)
 * 
 * @param text - Texto con coordenadas DMS
 * @returns ParsedCoordinate o null si no es válido
 * 
 * @example
 * tryParseDMS("37°11'03.5\"N 3°36'21.5\"W")
 * // => { lat: 37.184306, lon: -3.605972, format: 'DMS_GOOGLE', ... }
 */
export function tryParseDMS(text: string): ParsedCoordinate | null {
  if (!text || typeof text !== 'string') {
    return null;
  }
  
  const cleaned = text.trim();
  const warnings: string[] = [];
  
  // Intentar extraer par de coordenadas
  const pairMatch = cleaned.match(DMS_PAIR_PATTERN);
  
  if (pairMatch) {
    const latDMS: DMSComponents = {
      degrees: parseInt(pairMatch[1], 10),
      minutes: parseInt(pairMatch[2], 10),
      seconds: parseFloat(pairMatch[3].replace(',', '.')),
      direction: normalizeDirection(pairMatch[4]),
    };
    
    const lonDMS: DMSComponents = {
      degrees: parseInt(pairMatch[5], 10),
      minutes: parseInt(pairMatch[6], 10),
      seconds: parseFloat(pairMatch[7].replace(',', '.')),
      direction: normalizeDirection(pairMatch[8]),
    };
    
    // F2.01: Corregir overflow DMS
    const latFixed = fixDmsComponentsOverflow(latDMS);
    const lonFixed = fixDmsComponentsOverflow(lonDMS);
    if (latFixed || lonFixed) {
      warnings.push('DMS overflow corregido automáticamente');
    }
    
    const lat = dmsToDecimal(latDMS);
    const lon = dmsToDecimal(lonDMS);
    
    // Validar rangos
    if (!isInSpainBounds(lat, lon)) {
      warnings.push('Coordenadas fuera de España');
    }
    
    const format = detectDMSFormat(cleaned);
    const inHuso29 = isInHuso29Zone(lon);
    
    if (inHuso29) {
      warnings.push('Ubicación en zona Huso 29 (Huelva/oeste Sevilla)');
    }
    
    return {
      lat,
      lon,
      format,
      confidence: calculateConfidence(lat, lon, format),
      inHuso29Zone: inHuso29,
      originalText: cleaned,
      warnings: warnings.length > 0 ? warnings : undefined,
    };
  }
  
  // Intentar extraer coordenadas individuales
  const allMatches: DMSComponents[] = [];
  
  // Reset regex
  DMS_PATTERN.lastIndex = 0;
  let match;
  
  while ((match = DMS_PATTERN.exec(cleaned)) !== null) {
    const dms: DMSComponents = {
      degrees: parseInt(match[1], 10),
      minutes: parseInt(match[2], 10),
      seconds: parseFloat(match[3].replace(',', '.')),
      direction: normalizeDirection(match[4]),
    };
    fixDmsComponentsOverflow(dms); // F2.01
    allMatches.push(dms);
  }
  
  // Si no hay matches con símbolos, intentar con espacios
  if (allMatches.length === 0) {
    DMS_SPACES_PATTERN.lastIndex = 0;
    while ((match = DMS_SPACES_PATTERN.exec(cleaned)) !== null) {
      const dms: DMSComponents = {
        degrees: parseInt(match[1], 10),
        minutes: parseInt(match[2], 10),
        seconds: parseFloat(match[3].replace(',', '.')),
        direction: normalizeDirection(match[4]),
      };
      fixDmsComponentsOverflow(dms); // F2.01
      allMatches.push(dms);
    }
  }
  
  // Necesitamos al menos 2 coordenadas (lat + lon)
  if (allMatches.length < 2) {
    return null;
  }
  
  // Identificar latitud y longitud
  let latDMS: DMSComponents | null = null;
  let lonDMS: DMSComponents | null = null;
  
  for (const dms of allMatches) {
    if (dms.direction === 'N' || dms.direction === 'S') {
      if (!latDMS) latDMS = dms;
    } else if (dms.direction === 'E' || dms.direction === 'W') {
      if (!lonDMS) lonDMS = dms;
    }
  }
  
  if (!latDMS || !lonDMS) {
    return null;
  }
  
  const lat = dmsToDecimal(latDMS);
  const lon = dmsToDecimal(lonDMS);
  
  // Validar rangos
  if (!isInSpainBounds(lat, lon)) {
    warnings.push('Coordenadas fuera de España');
  }
  
  const format = detectDMSFormat(cleaned);
  const inHuso29 = isInHuso29Zone(lon);
  
  if (inHuso29) {
    warnings.push('Ubicación en zona Huso 29 (Huelva/oeste Sevilla)');
  }
  
  return {
    lat,
    lon,
    format,
    confidence: calculateConfidence(lat, lon, format),
    inHuso29Zone: inHuso29,
    originalText: cleaned,
    warnings: warnings.length > 0 ? warnings : undefined,
  };
}

/**
 * Valida si un texto contiene coordenadas DMS
 */
export function containsDMS(text: string): boolean {
  if (!text) return false;
  
  DMS_PATTERN.lastIndex = 0;
  return DMS_PATTERN.test(text) || DMS_SPACES_PATTERN.test(text);
}

// ============================================================================
// DD (DECIMAL DEGREES) PARSER - E04
// ============================================================================

/**
 * Patrones para coordenadas en grados decimales (DD)
 */

// DD con separador (coma o espacio): 37.184306, -3.605972 o 37.184306 -3.605972
const DD_COMMA_PATTERN = /^\s*(-?\d{1,3}(?:[.,]\d+)?)\s*[,;]\s*(-?\d{1,3}(?:[.,]\d+)?)\s*$/;
const DD_SPACE_PATTERN = /^\s*(-?\d{1,3}(?:[.,]\d+)?)\s+(-?\d{1,3}(?:[.,]\d+)?)\s*$/;

/**
 * Detecta si dos valores son lat/lon invertidos y necesitan swap
 * Heurística: Si el primer valor está fuera del rango de latitud España (35-44)
 * pero el segundo sí está, entonces están invertidos.
 * 
 * @param first - Primer valor parseado
 * @param second - Segundo valor parseado
 * @returns true si necesitan swap (lon,lat en vez de lat,lon)
 */
function needsLatLonSwap(first: number, second: number): boolean {
  const firstIsValidLat = first >= SPAIN_BOUNDS.lat.min && first <= SPAIN_BOUNDS.lat.max;
  const secondIsValidLat = second >= SPAIN_BOUNDS.lat.min && second <= SPAIN_BOUNDS.lat.max;
  const firstIsValidLon = first >= SPAIN_BOUNDS.lon.min && first <= SPAIN_BOUNDS.lon.max;
  
  // Si el primero NO es latitud válida pero SÍ es longitud válida,
  // y el segundo SÍ es latitud válida, entonces están invertidos
  if (!firstIsValidLat && firstIsValidLon && secondIsValidLat) {
    return true;
  }
  
  return false;
}

/**
 * Parsea coordenadas en formato Decimal Degrees (DD)
 * 
 * Formatos soportados:
 * - 37.184306, -3.605972 (coma)
 * - 37.184306; -3.605972 (punto y coma)
 * - 37.184306 -3.605972 (espacio)
 * - 37,184306, -3,605972 (coma decimal española)
 * 
 * Detecta automáticamente si las coordenadas están invertidas (lon, lat)
 * y las corrige.
 * 
 * @param text - Texto con coordenadas DD
 * @returns ParsedCoordinate o null si no es formato DD válido
 * 
 * @example
 * tryParseDD("37.184306, -3.605972")
 * // => { lat: 37.184306, lon: -3.605972, format: 'DD', ... }
 * 
 * tryParseDD("-3.605972, 37.184306") // Invertido
 * // => { lat: 37.184306, lon: -3.605972, format: 'DD', warnings: ['Coordenadas invertidas...'] }
 */
export function tryParseDD(text: string): ParsedCoordinate | null {
  if (!text || typeof text !== 'string') {
    return null;
  }
  
  const cleaned = text.trim();
  const warnings: string[] = [];
  
  // Intentar patrón con coma/punto y coma
  let match = cleaned.match(DD_COMMA_PATTERN);
  
  // Si no match, intentar patrón con espacio
  if (!match) {
    match = cleaned.match(DD_SPACE_PATTERN);
  }
  
  if (!match) {
    return null;
  }
  
  // Normalizar decimales (coma española a punto)
  const first = parseFloat(match[1].replace(',', '.'));
  const second = parseFloat(match[2].replace(',', '.'));
  
  if (isNaN(first) || isNaN(second)) {
    return null;
  }
  
  // Detectar si están invertidos (lon, lat en vez de lat, lon)
  let lat = first;
  let lon = second;
  let wasSwapped = false;
  
  if (needsLatLonSwap(first, second)) {
    lat = second;
    lon = first;
    wasSwapped = true;
    warnings.push('Coordenadas invertidas detectadas (lon,lat) - corregido automáticamente');
  }
  
  // Validar rangos
  if (!isInSpainBounds(lat, lon)) {
    warnings.push('Coordenadas fuera de España');
  }
  
  const inHuso29 = isInHuso29Zone(lon);
  if (inHuso29) {
    warnings.push('Ubicación en zona Huso 29 (Huelva/oeste Sevilla)');
  }
  
  const confidence = calculateConfidence(lat, lon, 'DD');
  
  return {
    lat,
    lon,
    format: 'DD',
    confidence: wasSwapped ? Math.max(confidence - 10, 50) : confidence, // Penalizar ligeramente si hubo swap
    inHuso29Zone: inHuso29,
    originalText: cleaned,
    warnings: warnings.length > 0 ? warnings : undefined,
  };
}

// ============================================================================
// UTM PARSER - E04
// ============================================================================

/**
 * Patrones para coordenadas UTM
 */

// UTM con etiquetas: X:447850 Y:4116234 o X=447850, Y=4116234
// B12: Soporta formato europeo con punto de miles (X:504.352,98 Y:4.077.905,68)
const UTM_LABELED_PATTERN = /[XxEe]\s*[:=]?\s*([\d][\d.,\s]*[\d]|\d+)\s*[,;\s]+\s*[YyNn]\s*[:=]?\s*([\d][\d.,\s]*[\d]|\d+)/;

// UTM directo: 447850.23, 4116234.56 (dos números grandes)
// B12: Soporta formato europeo con punto de miles (504.352,98, 4.077.905,68)
// Nota: El separador entre X e Y debe ser punto y coma (;) para evitar ambigüedad con puntos de miles
// O se puede usar coma si el formato es claro (ej: sin punto de miles o con espacios)
const UTM_DIRECT_PATTERN = /^\s*([\d][\d.,]*[\d]|\d+)\s*[;]\s*([\d][\d.,]*[\d]|\d+)\s*$/;

// UTM directo con coma: 447850.23, 4116234.56 (formato estándar sin punto miles)
const UTM_DIRECT_COMMA_PATTERN = /^\s*(\d{5,7}(?:[.,]\d+)?)\s*,\s*(\d{5,7}(?:[.,]\d+)?)\s*$/;

// UTM con espacio: 447850 4116234
// B12: Soporta formato europeo con espacios de miles (4 077 905)
// Nota: Espacios dentro de números se manejan por normalizeSpanishNumber
const UTM_SPACE_PATTERN = /^\s*(\d{5,7}(?:[.,]\d+)?)\s+(\d{5,7}(?:[.,]\d+)?)\s*$/;

/**
 * Valida si coordenadas UTM están en rango válido para Andalucía (Huso 30)
 */
export function isValidUtm30(x: number, y: number): boolean {
  return (
    x >= UTM30_ANDALUCIA_BOUNDS.x.min &&
    x <= UTM30_ANDALUCIA_BOUNDS.x.max &&
    y >= UTM30_ANDALUCIA_BOUNDS.y.min &&
    y <= UTM30_ANDALUCIA_BOUNDS.y.max
  );
}

/**
 * Valida si coordenadas UTM están en rango válido para Huso 29
 */
export function isValidUtm29(x: number, y: number): boolean {
  return (
    x >= UTM29_BOUNDS.x.min &&
    x <= UTM29_BOUNDS.x.max &&
    y >= UTM29_BOUNDS.y.min &&
    y <= UTM29_BOUNDS.y.max
  );
}

/**
 * Detecta si coordenadas UTM parecen estar invertidas (Y,X en vez de X,Y)
 * Heurística: Y (Norte) siempre es mayor que X (Este) en Andalucía
 */
function needsUtmSwap(first: number, second: number): boolean {
  // En Andalucía UTM30:
  // X (Este): 100000-620000
  // Y (Norte): 3980000-4290000
  // Si el segundo número es mucho más pequeño que el primero, están invertidos
  
  const firstLooksLikeY = first >= 3900000 && first <= 4300000;
  const secondLooksLikeX = second >= 80000 && second <= 700000;
  
  if (firstLooksLikeY && secondLooksLikeX) {
    return true;
  }
  
  return false;
}

/**
 * Parsea coordenadas UTM y las convierte a WGS84
 * 
 * Formatos soportados:
 * - 447850.23, 4116234.56 (X, Y directo)
 * - X:447850 Y:4116234 (etiquetado)
 * - X=447850, Y=4116234 (etiquetado con =)
 * - 447850 4116234 (espacio)
 * 
 * Asume Huso 30 (EPSG:25830) por defecto.
 * Detecta automáticamente si están invertidas.
 * 
 * @param text - Texto con coordenadas UTM
 * @returns ParsedCoordinate (convertido a WGS84) o null
 * 
 * @example
 * tryParseUTM("447850.23, 4116234.56")
 * // => { lat: 37.xxx, lon: -3.xxx, format: 'UTM', ... }
 */
export function tryParseUTM(text: string): ParsedCoordinate | null {
  if (!text || typeof text !== 'string') {
    return null;
  }
  
  const cleaned = text.trim();
  const warnings: string[] = [];
  
  let match: RegExpMatchArray | null;
  let wasLabeled = false;
  
  // Intentar patrón etiquetado primero (más específico)
  match = cleaned.match(UTM_LABELED_PATTERN);
  if (match) {
    wasLabeled = true;
  }
  
  // Si no, intentar patrón directo con punto y coma (B12: formato europeo)
  if (!match) {
    match = cleaned.match(UTM_DIRECT_PATTERN);
  }
  
  // Si no, intentar patrón directo con coma (formato estándar)
  if (!match) {
    match = cleaned.match(UTM_DIRECT_COMMA_PATTERN);
  }
  
  // Si no, intentar patrón con espacio
  if (!match) {
    match = cleaned.match(UTM_SPACE_PATTERN);
  }
  
  if (!match) {
    return null;
  }
  
  // B12: Normalizar formato europeo (punto miles, coma decimal)
  const xFormat = detectNumberFormat(match[1]);
  const yFormat = detectNumberFormat(match[2]);
  let x = normalizeSpanishNumber(match[1]);
  let y = normalizeSpanishNumber(match[2]);
  
  if (isNaN(x) || isNaN(y)) {
    return null;
  }
  
  // Añadir warning si se detectó formato europeo
  if (xFormat !== 'STANDARD' && xFormat !== 'UNKNOWN') {
    warnings.push(`Formato europeo detectado en X: ${xFormat}`);
  }
  if (yFormat !== 'STANDARD' && yFormat !== 'UNKNOWN') {
    warnings.push(`Formato europeo detectado en Y: ${yFormat}`);
  }
  
  // Detectar si están invertidos (Y,X en vez de X,Y)
  let wasSwapped = false;
  if (needsUtmSwap(x, y)) {
    [x, y] = [y, x];
    wasSwapped = true;
    warnings.push('Coordenadas UTM invertidas detectadas (Y,X) - corregido automáticamente');
  }
  
  // F2.02: Recuperación Y truncada (prefijo "4" perdido en Andalucía)
  const yRecovery = recoverTruncatedY({ x, y });
  if (yRecovery) {
    y = yRecovery.correctedY;
    warnings.push(`Y truncada recuperada: ${yRecovery.originalY} → ${yRecovery.correctedY} (${yRecovery.truncationType}, ${yRecovery.confidence})`);
  }
  
  // Validar rangos UTM
  const isValidUTM30 = isValidUtm30(x, y);
  const isValidUTM29 = isValidUtm29(x, y);
  
  if (!isValidUTM30 && !isValidUTM29) {
    warnings.push('Coordenadas UTM fuera del rango de Andalucía');
  }
  
  // Convertir a WGS84
  // Usar Huso 30 por defecto, o Huso 29 si las coordenadas son válidas para ese huso
  let epsg: 'EPSG:25830' | 'EPSG:25829' = 'EPSG:25830';
  if (isValidUTM29 && !isValidUTM30) {
    epsg = 'EPSG:25829';
  }
  
  try {
    const wgs = fromUtm(x, y, epsg);
    const lat = wgs.lat;
    const lon = wgs.lng;
    
    // Validar resultado
    if (!isInSpainBounds(lat, lon)) {
      warnings.push('Resultado de conversión fuera de España');
    }
    
    const inHuso29 = isInHuso29Zone(lon);
    if (inHuso29) {
      warnings.push('Ubicación en zona Huso 29 (Huelva/oeste Sevilla)');
    }
    
    let confidence = calculateConfidence(lat, lon, 'UTM');
    if (wasSwapped) confidence = Math.max(confidence - 10, 50);
    if (wasLabeled) confidence = Math.min(confidence + 5, 100); // Bonus por etiquetas explícitas
    if (yRecovery) confidence = Math.max(confidence - (yRecovery.confidence === 'high' ? 5 : 15), 40);
    
    return {
      lat,
      lon,
      format: 'UTM',
      confidence,
      inHuso29Zone: inHuso29,
      originalText: cleaned,
      warnings: warnings.length > 0 ? warnings : undefined,
    };
  } catch (_error) {
    // Error en conversión proj4
    return null;
  }
}

// ============================================================================
// MAIN PARSER FUNCTION - E04
// ============================================================================

/**
 * Parser principal que intenta todos los formatos de coordenadas
 * 
 * Orden de precedencia:
 * 1. DMS (más específico por símbolos ° ' ")
 * 2. UTM (números grandes característicos)
 * 3. DD (formato más simple/genérico)
 * 
 * @param text - Texto con coordenadas en cualquier formato
 * @returns ParsedCoordinate o null si no se puede parsear
 * 
 * @example
 * parseCoordinateInput("37°11'03.5\"N 3°36'21.5\"W") // DMS
 * parseCoordinateInput("37.184306, -3.605972")         // DD
 * parseCoordinateInput("447850, 4116234")              // UTM
 */
export function parseCoordinateInput(text: string): ParsedCoordinate | null {
  if (!text || typeof text !== 'string') {
    return null;
  }
  
  // F2.01: Pre-procesamiento OCR → corregir letras confundidas con dígitos
  let cleaned = text.trim();
  let ocrApplied = false;
  if (hasOCRErrors(cleaned)) {
    const fixed = fixOCR(cleaned);
    if (fixed !== cleaned) {
      ocrApplied = true;
      cleaned = fixed;
    }
  }

  // F2.01: Pre-procesamiento DMS overflow → corregir min/seg ≥60
  let _dmsOverflowApplied = false;
  if (hasDmsOverflow(cleaned)) {
    const overflow = correctDmsOverflow(cleaned);
    if (overflow) {
      _dmsOverflowApplied = true;
      cleaned = overflow.normalized;
    }
  }
  
  // 1. Intentar DMS primero (más específico por símbolos)
  const dmsResult = tryParseDMS(cleaned);
  if (dmsResult) {
    if (ocrApplied) {
      dmsResult.warnings = [...(dmsResult.warnings ?? []), 'OCR corregido automáticamente'];
      dmsResult.confidence = Math.max(dmsResult.confidence - 5, 50);
    }
    return dmsResult;
  }
  
  // 2. Intentar UTM (números grandes característicos)
  // Detectar si parece UTM (números de 5-7 dígitos)
  if (/\d{5,7}/.test(cleaned)) {
    const utmResult = tryParseUTM(cleaned);
    if (utmResult) {
      if (ocrApplied) {
        utmResult.warnings = [...(utmResult.warnings ?? []), 'OCR corregido automáticamente'];
        utmResult.confidence = Math.max(utmResult.confidence - 5, 50);
      }
      return utmResult;
    }
  }
  
  // 3. Intentar DD (formato más genérico)
  const ddResult = tryParseDD(cleaned);
  if (ddResult) {
    if (ocrApplied) {
      ddResult.warnings = [...(ddResult.warnings ?? []), 'OCR corregido automáticamente'];
      ddResult.confidence = Math.max(ddResult.confidence - 5, 50);
    }
    return ddResult;
  }
  
  // F2.02: Intentar separar coordenadas concatenadas (fallback final)
  const splitResult = splitConcatenatedCoords(cleaned);
  if (splitResult) {
    // Tenemos X/Y separadas → parsear como UTM
    const concatUtm = tryParseUTM(`${splitResult.x} ${splitResult.y}`);
    if (concatUtm) {
      concatUtm.warnings = [
        ...(concatUtm.warnings ?? []),
        `Coordenadas concatenadas separadas: "${splitResult.originalInput}" → X=${splitResult.x}, Y=${splitResult.y} (${splitResult.format}, ${splitResult.confidence})`,
      ];
      // Penalizar confianza según la confianza del split
      const penalty = splitResult.confidence === 'high' ? 5 : splitResult.confidence === 'medium' ? 10 : 20;
      concatUtm.confidence = Math.max(concatUtm.confidence - penalty, 30);
      if (ocrApplied) {
        concatUtm.warnings.push('OCR corregido automáticamente');
        concatUtm.confidence = Math.max(concatUtm.confidence - 5, 30);
      }
      return concatUtm;
    }
  }

  // M5: PlusCode (Open Location Code) — detector offline, sin API externa
  const plusCodeResult = tryParsePlusCode(cleaned);
  if (plusCodeResult) {
    return plusCodeResult;
  }

  return null;
}

/**
 * Detecta el formato de coordenadas sin parsear completamente
 * 
 * @param text - Texto a analizar
 * @returns Formato detectado o 'UNKNOWN'
 */
export function detectCoordinateFormat(text: string): CoordinateFormat {
  if (!text) return 'UNKNOWN';
  
  const cleaned = text.trim();
  
  // DMS: tiene símbolos de grado/minuto/segundo
  if (containsDMS(cleaned)) {
    return detectDMSFormat(cleaned);
  }
  
  // UTM: números de 5-7 dígitos (X ~100000-620000, Y ~3980000-4290000)
  if (/\d{6,7}/.test(cleaned) && /\d{5,6}/.test(cleaned)) {
    return 'UTM';
  }
  
  // DD: dos números decimales separados
  if (DD_COMMA_PATTERN.test(cleaned) || DD_SPACE_PATTERN.test(cleaned)) {
    return 'DD';
  }
  
  return 'UNKNOWN';
}

// ============================================================================
// CRIT-04: SEPARAR COORDENADAS PEGADAS
// ============================================================================

/**
 * Separa coordenadas UTM pegadas sin separador.
 * 
 * Ejemplos de entrada:
 * - "4077905504750" (13 dígitos) → Y=4077905, X=504750
 * - "407790599500" (12 dígitos) → Y=4077905, X=99500
 * 
 * Heurística basada en rangos UTM de Andalucía:
 * - Y (Northing): 3.980.000 - 4.290.000 (7 dígitos, empieza por 39-42)
 * - X (Easting): 100.000 - 620.000 (5-6 dígitos)
 * 
 * @param valor - Cadena con coordenadas pegadas
 * @returns Objeto {x, y} si se pudo separar, null si no
 * 
 * @example
 * separarCoordenadasPegadas("4077905504750") // { x: 504750, y: 4077905 }
 * separarCoordenadasPegadas("Centro Salud")   // null
 */
export function separarCoordenadasPegadas(valor: string): { x: number; y: number } | null {
  if (!valor || typeof valor !== 'string') {
    return null;
  }

  // Limpiar: quitar espacios, puntos de miles, comas
  const cleaned = valor.replace(/[\s.,]/g, '');
  
  // Verificar que solo queden dígitos
  if (!/^\d+$/.test(cleaned)) {
    return null;
  }

  const len = cleaned.length;
  
  // Rango válido: 10-14 dígitos (Y de 7 dígitos + X de 3-7 dígitos)
  // Realista para Andalucía: 12-13 dígitos
  if (len < 10 || len > 14) {
    return null;
  }

  // Estrategia: Y siempre tiene 7 dígitos en Andalucía (39xxxxx a 42xxxxx)
  const Y_DIGITS = 7;
  
  // Extraer Y (primeros 7 dígitos) y X (resto)
  const yStr = cleaned.substring(0, Y_DIGITS);
  const xStr = cleaned.substring(Y_DIGITS);
  
  // Validar que X tenga dígitos
  if (xStr.length === 0) {
    return null;
  }
  
  const y = parseInt(yStr, 10);
  const x = parseInt(xStr, 10);
  
  // Validar rangos Andalucía UTM30
  // Y: 3.980.000 - 4.290.000
  if (y < UTM30_ANDALUCIA_BOUNDS.y.min || y > UTM30_ANDALUCIA_BOUNDS.y.max) {
    return null;
  }
  
  // X: 100.000 - 620.000 (para coordenadas estándar)
  // Nota: X puede ser < 100000 en casos raros del borde oeste
  if (x < 10000 || x > UTM30_ANDALUCIA_BOUNDS.x.max) {
    return null;
  }

  return { x, y };
}

// ============================================================================
// EXT-06: EXTRACCIÓN DE COORDENADAS EN TEXTO LIBRE
// ============================================================================

/**
 * Resultado de extracción de coordenadas de texto libre
 */
export interface FreeTextCoordinateMatch {
  /** Coordenadas parseadas (lat/lon en WGS84) */
  coordinates: ParsedCoordinate;
  /** Posición inicial en el texto original */
  startIndex: number;
  /** Posición final en el texto original */
  endIndex: number;
  /** Fragmento de texto que contiene las coordenadas */
  matchedText: string;
  /** Contexto alrededor del match (±30 caracteres) */
  context: string;
}

/**
 * Patrones para detectar coordenadas UTM en texto libre
 * Más flexibles que los patrones de parsing directo
 */

// UTM etiquetado: X:447850 Y:4116234, X=447850, Y=4116234, etc.
// Rango flexible 5-7 dígitos para ambos (tryParseUtmPair detecta inversión)
const UTM_LABELED_FREE_TEXT = /[XxEe]\s*[:=]?\s*(\d{5,7}(?:[.,]\d+)?)\s*[,;\s]+\s*[YyNn]\s*[:=]?\s*(\d{5,7}(?:[.,]\d+)?)/g;

// UTM con prefijo "coordenadas": "coordenadas 447850 4116234" o "coords: 447850, 4116234"
const UTM_COORDS_PREFIX = /(?:coordenadas?|coords?\.?|ubicaci[oó]n|posici[oó]n)\s*:?\s*(\d{5,6}(?:[.,]\d+)?)\s*[,;\s]+\s*(\d{6,7}(?:[.,]\d+)?)/gi;

// UTM entre paréntesis: (447850, 4116234) o (X:447850, Y:4116234)
const UTM_PARENTHESES = /\(\s*(?:[XxEe]\s*[:=]?\s*)?(\d{5,6}(?:[.,]\d+)?)\s*[,;\s]+\s*(?:[YyNn]\s*[:=]?\s*)?(\d{6,7}(?:[.,]\d+)?)\s*\)/g;

// DD con prefijo: "coordenadas 37.17, -3.59" o similar
const DD_COORDS_PREFIX = /(?:coordenadas?|coords?\.?|latitud|longitud|lat\/lon|ubicaci[oó]n)\s*:?\s*(-?\d{1,3}[.,]\d{3,8})\s*[,;\s]+\s*(-?\d{1,3}[.,]\d{3,8})/gi;

// DMS en texto: "ubicado en 37°10'37"N 3°35'24"W"
const DMS_FREE_TEXT = new RegExp(
  `(\\d{1,2})\\s*${DEG_SYMBOLS}\\s*` +
  `(\\d{1,2})\\s*${MIN_SYMBOLS}\\s*` +
  `(\\d{1,2}(?:[.,]\\d+)?)\\s*(?:${SEC_SYMBOLS})?\\s*` +
  `([NS])` +
  `[\\s,]+` +
  `(\\d{1,3})\\s*${DEG_SYMBOLS}\\s*` +
  `(\\d{1,2})\\s*${MIN_SYMBOLS}\\s*` +
  `(\\d{1,2}(?:[.,]\\d+)?)\\s*(?:${SEC_SYMBOLS})?\\s*` +
  `([EWO])`,
  'gi'
);

/**
 * Extrae el contexto alrededor de un match (±30 caracteres)
 */
function getMatchContext(text: string, startIndex: number, endIndex: number): string {
  const contextStart = Math.max(0, startIndex - 30);
  const contextEnd = Math.min(text.length, endIndex + 30);
  
  let context = '';
  if (contextStart > 0) context += '...';
  context += text.substring(contextStart, contextEnd);
  if (contextEnd < text.length) context += '...';
  
  return context;
}

/**
 * Intenta parsear un par de valores como coordenadas UTM
 */
function tryParseUtmPair(first: string, second: string): ParsedCoordinate | null {
  // B12: Normalizar formato europeo
  const x = normalizeSpanishNumber(first);
  const y = normalizeSpanishNumber(second);
  
  if (isNaN(x) || isNaN(y)) return null;
  
  // Detectar si están invertidos (Y,X en vez de X,Y)
  let finalX = x;
  let finalY = y;
  let wasSwapped = false;
  
  // Y siempre es mayor que X en Andalucía
  if (x >= 3900000 && x <= 4300000 && y >= 80000 && y <= 700000) {
    finalX = y;
    finalY = x;
    wasSwapped = true;
  }
  
  // Validar rangos UTM
  if (!isValidUtm30(finalX, finalY) && !isValidUtm29(finalX, finalY)) {
    return null;
  }
  
  // Determinar EPSG
  let epsg: 'EPSG:25830' | 'EPSG:25829' = 'EPSG:25830';
  if (isValidUtm29(finalX, finalY) && !isValidUtm30(finalX, finalY)) {
    epsg = 'EPSG:25829';
  }
  
  try {
    const wgs = fromUtm(finalX, finalY, epsg);
    const warnings: string[] = [];
    
    if (wasSwapped) {
      warnings.push('Coordenadas UTM invertidas detectadas (Y,X) - corregido automáticamente');
    }
    
    const inHuso29 = isInHuso29Zone(wgs.lng);
    if (inHuso29) {
      warnings.push('Ubicación en zona Huso 29 (Huelva/oeste Sevilla)');
    }
    
    return {
      lat: wgs.lat,
      lon: wgs.lng,
      format: 'UTM',
      confidence: wasSwapped ? 75 : 85,
      inHuso29Zone: inHuso29,
      originalText: `${first}, ${second}`,
      warnings: warnings.length > 0 ? warnings : undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Intenta parsear un par de valores como coordenadas DD (grados decimales)
 */
function tryParseDdPair(first: string, second: string): ParsedCoordinate | null {
  // B12: Normalizar formato europeo (aunque menos común en DD)
  let lat = normalizeSpanishNumber(first);
  let lon = normalizeSpanishNumber(second);
  
  if (isNaN(lat) || isNaN(lon)) return null;
  
  const warnings: string[] = [];
  
  // Detectar si están invertidos
  if (needsLatLonSwap(lat, lon)) {
    [lat, lon] = [lon, lat];
    warnings.push('Coordenadas invertidas detectadas (lon,lat) - corregido automáticamente');
  }
  
  // Validar rangos España
  if (!isInSpainBounds(lat, lon)) {
    return null; // Rechazar si está fuera de España completamente
  }
  
  const inHuso29 = isInHuso29Zone(lon);
  if (inHuso29) {
    warnings.push('Ubicación en zona Huso 29 (Huelva/oeste Sevilla)');
  }
  
  return {
    lat,
    lon,
    format: 'DD',
    confidence: isInAndaluciaBounds(lat, lon) ? 90 : 70,
    inHuso29Zone: inHuso29,
    originalText: `${first}, ${second}`,
    warnings: warnings.length > 0 ? warnings : undefined,
  };
}

/**
 * Extrae todas las coordenadas encontradas en un texto libre.
 * 
 * Busca patrones de coordenadas UTM, DD y DMS dentro de texto descriptivo,
 * campos de observaciones, direcciones, etc.
 * 
 * @param text - Texto libre donde buscar coordenadas
 * @returns Array de coincidencias encontradas con posición y coordenadas parseadas
 * 
 * @example
 * extractCoordinatesFromFreeText("El centro está en coordenadas 447850, 4116234 junto a la plaza")
 * // => [{ coordinates: {...}, startIndex: 25, endIndex: 41, matchedText: "447850, 4116234", ... }]
 * 
 * @example
 * extractCoordinatesFromFreeText("Ubicación: X:447850 Y:4116234 (aproximada)")
 * // => [{ coordinates: {...}, startIndex: 11, endIndex: 29, matchedText: "X:447850 Y:4116234", ... }]
 * 
 * @task EXT-06
 */
export function extractCoordinatesFromFreeText(text: string): FreeTextCoordinateMatch[] {
  if (!text || typeof text !== 'string' || text.length < 10) {
    return [];
  }
  
  const matches: FreeTextCoordinateMatch[] = [];
  const usedRanges: Array<{start: number; end: number}> = [];
  
  // Helper: verificar si un rango ya está usado (evitar duplicados)
  function isRangeUsed(start: number, end: number): boolean {
    return usedRanges.some(r => 
      (start >= r.start && start < r.end) || 
      (end > r.start && end <= r.end) ||
      (start <= r.start && end >= r.end)
    );
  }
  
  // Helper: añadir match si las coordenadas son válidas
  function addMatch(
    coordinates: ParsedCoordinate | null,
    matchedText: string,
    startIndex: number,
    endIndex: number
  ): void {
    if (!coordinates) return;
    if (isRangeUsed(startIndex, endIndex)) return;
    
    usedRanges.push({ start: startIndex, end: endIndex });
    matches.push({
      coordinates,
      startIndex,
      endIndex,
      matchedText,
      context: getMatchContext(text, startIndex, endIndex),
    });
  }
  
  // 1. Buscar DMS (más específico, procesar primero)
  DMS_FREE_TEXT.lastIndex = 0;
  let dmsMatch;
  while ((dmsMatch = DMS_FREE_TEXT.exec(text)) !== null) {
    const parsed = tryParseDMS(dmsMatch[0]);
    if (parsed) {
      addMatch(parsed, dmsMatch[0], dmsMatch.index, dmsMatch.index + dmsMatch[0].length);
    }
  }
  
  // 2. Buscar UTM etiquetado (X:, Y:)
  UTM_LABELED_FREE_TEXT.lastIndex = 0;
  let utmLabeledMatch;
  while ((utmLabeledMatch = UTM_LABELED_FREE_TEXT.exec(text)) !== null) {
    const parsed = tryParseUtmPair(utmLabeledMatch[1], utmLabeledMatch[2]);
    if (parsed) {
      addMatch(parsed, utmLabeledMatch[0], utmLabeledMatch.index, utmLabeledMatch.index + utmLabeledMatch[0].length);
    }
  }
  
  // 3. Buscar UTM con prefijo "coordenadas"
  UTM_COORDS_PREFIX.lastIndex = 0;
  let utmPrefixMatch;
  while ((utmPrefixMatch = UTM_COORDS_PREFIX.exec(text)) !== null) {
    const parsed = tryParseUtmPair(utmPrefixMatch[1], utmPrefixMatch[2]);
    if (parsed) {
      addMatch(parsed, utmPrefixMatch[0], utmPrefixMatch.index, utmPrefixMatch.index + utmPrefixMatch[0].length);
    }
  }
  
  // 4. Buscar UTM entre paréntesis
  UTM_PARENTHESES.lastIndex = 0;
  let utmParenMatch;
  while ((utmParenMatch = UTM_PARENTHESES.exec(text)) !== null) {
    const parsed = tryParseUtmPair(utmParenMatch[1], utmParenMatch[2]);
    if (parsed) {
      addMatch(parsed, utmParenMatch[0], utmParenMatch.index, utmParenMatch.index + utmParenMatch[0].length);
    }
  }
  
  // 5. Buscar DD con prefijo
  DD_COORDS_PREFIX.lastIndex = 0;
  let ddPrefixMatch;
  while ((ddPrefixMatch = DD_COORDS_PREFIX.exec(text)) !== null) {
    const parsed = tryParseDdPair(ddPrefixMatch[1], ddPrefixMatch[2]);
    if (parsed) {
      addMatch(parsed, ddPrefixMatch[0], ddPrefixMatch.index, ddPrefixMatch.index + ddPrefixMatch[0].length);
    }
  }
  
  // Ordenar por posición en el texto
  matches.sort((a, b) => a.startIndex - b.startIndex);
  
  return matches;
}

/**
 * Verifica si un texto contiene coordenadas extraíbles.
 * Versión rápida sin parseo completo.
 * 
 * @param text - Texto a verificar
 * @returns true si el texto parece contener coordenadas
 * 
 * @task EXT-06
 */
export function containsExtractableCoordinates(text: string): boolean {
  if (!text || typeof text !== 'string' || text.length < 10) {
    return false;
  }
  
  // Verificaciones rápidas (sin regex complejos)
  
  // 1. Contiene símbolos DMS
  if (containsDMS(text)) return true;
  
  // 2. Contiene etiquetas X/Y
  if (/[XxEe]\s*[:=]\s*\d{5,6}/.test(text)) return true;
  
  // 3. Contiene palabra "coordenadas" seguida de números
  if (/coordenadas?\s*:?\s*\d{5,6}/i.test(text)) return true;
  
  // 4. Contiene paréntesis con números grandes (UTM)
  if (/\(\s*\d{5,6}\s*[,;]\s*\d{6,7}\s*\)/.test(text)) return true;
  
  // 5. Contiene "ubicación" o "posición" con números
  if (/(?:ubicaci[oó]n|posici[oó]n)\s*:?\s*\d{5,6}/i.test(text)) return true;
  
  return false;
}

// ============================================================================
// M5: PLUSCODE (Open Location Code / Google)
// ============================================================================

/**
 * Regex para detectar un PlusCode (OLC) en un texto.
 *
 * Formato: 4–8 chars del alfabeto OLC + '+' + 2–7 chars OLC
 * Ejemplos válidos: "8C9P2Q+XC"  "8FVC9G8F+6W"  "9G8F+6W"
 * Alfabeto OLC: 23456789CFGHJMPQRVWX (sin vocales ni confundibles)
 */
const OLC_ALPHABET = '23456789CFGHJMPQRVWXcfghjmpqrvwx';
const PLUSCODE_REGEX = new RegExp(
  `(?:^|[\\s,;:])([${OLC_ALPHABET}]{4,8}\\+[${OLC_ALPHABET}]{2,7})(?:[\\s,;:]|$)`,
  'i'
);

/**
 * Extrae el código OLC puro de un texto que puede contener localidad.
 * Ej: "8C9P2Q+XC Córdoba" → "8C9P2Q+XC"
 */
function extractOLCCode(text: string): string | null {
  const m = PLUSCODE_REGEX.exec(` ${text} `);
  return m ? m[1].toUpperCase() : null;
}

/**
 * M5: Parsea un PlusCode (Open Location Code) a lat/lon WGS84.
 *
 * @param text - Texto que puede contener un PlusCode, con o sin localidad
 * @returns ParsedCoordinate o null si no se detecta un PlusCode válido y completo
 *
 * @example
 * tryParsePlusCode("8C9P2Q+XC")           // → { lat: 37.38..., lon: -5.99..., format: 'PLUSCODE' }
 * tryParsePlusCode("8C9P2Q+XC Córdoba")   // → mismo resultado
 * tryParsePlusCode("no es un código")      // → null
 */
export function tryParsePlusCode(text: string): ParsedCoordinate | null {
  if (!text || typeof text !== 'string') return null;

  const code = extractOLCCode(text.trim());
  if (!code) return null;

  // Validar con la librería: debe ser válido Y completo (full code)
  if (!_olc.isValid(code) || !_olc.isFull(code)) return null;

  try {
    const area = _olc.decode(code);
    const lat = area.latitudeCenter;
    const lon = area.longitudeCenter;

    // Sanity check: debe estar dentro de España (coordenadas razonables)
    if (!isInSpainBounds(lat, lon)) {
      return null;
    }

    return {
      lat,
      lon,
      format: 'PLUSCODE',
      confidence: 90,
      inHuso29Zone: isInHuso29Zone(lon),
      originalText: text,
      warnings: code !== text.trim() ? [`PlusCode extraído: "${code}"`] : undefined,
    };
  } catch {
    return null;
  }
}



export default {
  // Main parser (E04)
  parseCoordinateInput,
  detectCoordinateFormat,
  // Individual parsers
  tryParseDMS,
  tryParseDD,
  tryParseUTM,
  tryParsePlusCode,
  separarCoordenadasPegadas,
  // B12: Formato europeo
  normalizeSpanishNumber,
  detectNumberFormat,
  // Free text extraction (EXT-06)
  extractCoordinatesFromFreeText,
  containsExtractableCoordinates,
  // DMS helpers
  parseSingleDMS,
  dmsToDecimal,
  normalizeDirection,
  detectDMSFormat,
  containsDMS,
  // UTM helpers (E04)
  isValidUtm30,
  isValidUtm29,
  // Validation
  isInSpainBounds,
  isInAndaluciaBounds,
  isInHuso29Zone,
  // Constants
  SPAIN_BOUNDS,
  ANDALUCIA_BOUNDS,
  HUSO_29_MERIDIAN,
  UTM30_ANDALUCIA_BOUNDS,
  UTM29_BOUNDS,
};
